import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { createNameRenderer } from '@/lib/utils/tableRenderers'

type TabValue = 'all' | 'trash'

export default function BookingManagementPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isCompletedModalOpen, onOpen: onOpenCompletedModal, onClose: onCloseCompletedModal } = useDisclosure()
  const {
    isOpen: isRowDeletionNotAllowedOpen,
    onOpen: onOpenRowDeletionNotAllowed,
    onClose: onCloseRowDeletionNotAllowed,
  } = useDisclosure()
  const {
    isOpen: isBulkDeletionNotAllowedOpen,
    onOpen: onOpenBulkDeletionNotAllowed,
    onClose: onCloseBulkDeletionNotAllowed,
  } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const onBookingRowDeleteNotAllowed = useCallback(() => {
    onOpenRowDeletionNotAllowed()
  }, [onOpenRowDeletionNotAllowed])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        reference_number: {
          name: 'Reference No',
          maxWidth: '100px',
        },
        shipping_line: {
          name: 'Shipping Line',
          maxWidth: '200px',
          withSort: true,
          sortField: 'shipping_line_id',
        },
        cypa_from: {
          name: 'Pickup',
          maxWidth: '200px',
          withSort: true,
          sortField: 'cypa_id_from',
        },
        cypa_to: {
          name: 'Dropoff',
          maxWidth: '200px',
          withSort: true,
          sortField: 'cypa_id_to',
        },
        vessel: {
          name: 'Vessel',
          maxWidth: '100px',
          withSort: true,
        },
        expected_date: {
          name: 'Expected Date',
          maxWidth: '90px',
          withSort: true,
        },
        expected_container: {
          name: 'Expected Container/s',
          maxWidth: '60px',
        },
        remaining_container: {
          name: 'Remaining Container/s',
          maxWidth: '60px',
        },
        is_ship_in: {
          name: 'Remarks',
          maxWidth: '80px',
        },
        is_complete: {
          name: 'Status',
          maxWidth: '80px',  
        },
        prepared_by: {
          name: "Prepared By",
          maxWidth: "150px",
          withSort: true,
        },
      },
      dataSource: activeTab === 'trash' ? '/archived/bookings' : '/bookings',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: true,
      noRecordText: 'No booking found',
      otherActions: [
        {
          name: 'Waybills',
          onClick: (row: any) => {
            navigate(`/bookings/waybills/${row.id}`)
          },
        },
      ],
      isDeleteAllowedForRow: (row: any) => !(row?.is_complete === 1 || row?.is_complete === true),
      onDeleteNotAllowed: onBookingRowDeleteNotAllowed,
    }),
    [activeTab, onBookingRowDeleteNotAllowed]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access]
  )

  const customRenderers = useMemo(
    () => ({
      reference_number: (value: any) => {
        return (
          <Box
            maxW="100px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      shipping_line: createNameRenderer(true),
      cypa_from: createNameRenderer(true),
      cypa_to: createNameRenderer(true),
      vessel: (value: any) => {
        return (
          <Box
            maxW="100px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      is_complete: (value: any) => {
        const isComplete = value === 1 || value === true
        return (
          <Box width="100%" display="flex" justifyContent="center">
            <Badge colorScheme={isComplete ? 'green' : 'yellow'} minWidth="80px" textAlign="center">
              {isComplete ? 'Closed' : 'Open'}
            </Badge>
          </Box>
        )
      },
      is_ship_in: (value: any) => {
        const isShipIn = value === 1 || value === true
        return (
          <Box width="100%" display="flex" justifyContent="center">
            <Badge colorScheme={isShipIn ? 'blue' : 'gray'} minWidth="80px" textAlign="center">
              {isShipIn ? 'Ship In' : 'Ship Out'}
            </Badge>
          </Box>
        )
      },
      remaining_container: (value: any) => {
        const numValue = Number(value)
        const isNegative = numValue < 0
        return (
          <Text color={isNegative ? 'red.500' : 'inherit'}>
            {value ?? ''}
          </Text>
        )
      },
    }),
    []
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = async () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete') {
      const selectedRows = tableRef.current?.getSelectedRowsData() || []
      if (selectedRows.some((r) => r?.is_complete === 1 || r?.is_complete === true)) {
        onOpenBulkDeletionNotAllowed()
        return
      }
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    if (bulkAction === 'delete') {
      const selectedRows = tableRef.current?.getSelectedRowsData() || []
      if (selectedRows.some((r) => r?.is_complete === 1 || r?.is_complete === true)) {
        onOpenBulkDeletionNotAllowed()
        return
      }
    }

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/bookings/bulk/restore'
        successMessage = 'Bookings restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/bookings/bulk/force-delete'
            : '/bookings/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Bookings permanently deleted successfully' : 'Bookings moved to trash successfully'
      } else {
        showWarning('Unsupported bulk action')
        setIsBulkProcessing(false)
        return
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  // Auto-switch to All tab when Trash becomes empty
  useEffect(() => {
    if (activeTab === 'trash' && totalTrash === 0) {
      // Switch to All tab and refresh
      setActiveTab('all')
      setSearchQuery('')
      if (searchRef.current) {
        searchRef.current.value = ''
      }
      tableRef.current?.clearPage()
      // Small delay to ensure tab switch completes before reload
      setTimeout(() => {
        tableRef.current?.reload()
      }, 100)
    }
  }, [totalTrash, activeTab])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Booking Management
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage bookings and waybills
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/bookings"
                      createLabel="Create Booking"
                    />
                  </Box>

                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(row: any) => {
                    const isComplete = row?.is_complete === 1 || row?.is_complete === true
                    if (isComplete) {
                      onOpenCompletedModal()
                    } else {
                      navigate(`/bookings/${row.id}`)
                    }
                  }}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isCompletedModalOpen} onClose={onCloseCompletedModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cannot edit booking</ModalHeader>
          <ModalBody>
            <Text>This booking is completed and cannot be edited.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onCloseCompletedModal}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isRowDeletionNotAllowedOpen} onClose={onCloseRowDeletionNotAllowed} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Deletion not allowed</ModalHeader>
          <ModalBody>
            <Text>This booking is already completed. Deletion is not allowed.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onCloseRowDeletionNotAllowed}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBulkDeletionNotAllowedOpen} onClose={onCloseBulkDeletionNotAllowed} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Deletion not allowed</ModalHeader>
          <ModalBody>
            <Text>
              Please check your selection and make sure there are no completed bookings included. Deletion is not allowed
              for completed bookings.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onCloseBulkDeletionNotAllowed}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
